import { BasicDialogWindow, Dialog, DialogButton } from '../../../../widgets/dialog';
import { FormControl } from '../../../../widgets/formControl';
import { Input } from '../../../../widgets/input';
import { SubSection } from '../../../../widgets/section';
import Stack from '../../../../widgets/stack/stack';
import React, { FC, useState } from 'react';
import { T } from 'src/shared/helpers';
import { ModelColor } from '../../state/model/modelColor';
import { Side } from '../../state/side';

interface JSONSettingsDialogProps {
    modelColor: ModelColor;
    sides: Side[];
    onClose: () => void;
    onSave: (newModelColor: { settings: { colorProviderCode: string | null; sideCodes: { [sideGuid: string]: string } } }) => void;
}

const JSONSettingsDialog: FC<JSONSettingsDialogProps> = ({ modelColor, sides, onClose, onSave }) => {
    const [colorProviderCode, setColorProviderCode] = useState<string>(modelColor.colorProviderCode || '');
    const [sideCodes, setSideCodes] = useState<{ [sideGuid: string]: string }>(() => {
        const initialSideCodes: { [sideGuid: string]: string } = {};
        modelColor.sides.forEach((side) => {
            initialSideCodes[side.guid] = side.code || '';
        });
        return initialSideCodes;
    });

    const handleSideCodeChange = (sideGuid: string, value: string) => {
        setSideCodes((prev) => ({
            ...prev,
            [sideGuid]: value
        }));
    };

    const handleSave = () => {
        const trimmedSideCodes: { [sideGuid: string]: string } = {};
        Object.keys(sideCodes).forEach((sideGuid) => {
            trimmedSideCodes[sideGuid] = sideCodes[sideGuid].trim();
        });

        onSave({
            settings: {
                colorProviderCode: colorProviderCode.trim() === '' ? null : colorProviderCode.trim(),
                sideCodes: trimmedSideCodes
            }
        });
    };

    const buttons: DialogButton[] = [
        {
            label: T._('Cancel', 'Admin'),
            outline: true,
            onClick: onClose
        },
        {
            label: T._('Save', 'Admin'),
            outline: false,
            onClick: handleSave
        }
    ];

    return (
        <Dialog windowDecorator={BasicDialogWindow} title={T._('Bulk import settings', 'Admin')} buttons={buttons}>
            <Stack>
                <SubSection title={T._('Color', 'Admin')}>
                    <FormControl label={T._('Color Provider Code', 'Admin')}>
                        <Input
                            type='text'
                            value={colorProviderCode}
                            onChange={(e) => setColorProviderCode(e.target.value)}
                            placeholder={T._('Enter color provider code', 'Admin')}
                        />
                    </FormControl>
                </SubSection>

                <SubSection title={T._('Sides', 'Admin')} contentGap='15px'>
                    {modelColor.sides.map((side, index) => {
                        const originalSide = sides.find((s) => s.guid === side.originalSideGuid);
                        const sideName = originalSide ? originalSide.name : `${T._('Side', 'Admin')} ${index + 1}`;
                        return (
                            <FormControl key={side.guid} label={sideName}>
                                <Input
                                    type='text'
                                    value={sideCodes[side.guid] || ''}
                                    onChange={(e) => handleSideCodeChange(side.guid, e.target.value)}
                                    placeholder={T._('Enter side code', 'Admin')}
                                />
                            </FormControl>
                        );
                    })}
                </SubSection>
            </Stack>
        </Dialog>
    );
};

export default JSONSettingsDialog;
